import {
  type AssetFolder,
  type AssetKind,
  childFolders,
  FOLDER_NAME_MAX_LENGTH,
  folderPath,
  folderTree,
  MEDIA_ROOT_LABEL,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Folder, FolderOpen, Images } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { orpc } from "../lib/api";
import { cn, errorMessage } from "../lib/utils";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import { Field, Input, Select } from "./ui/input";

/** Drag data of media tiles and folder tiles in the media library. */
export const ASSETS_DRAG_TYPE = "application/x-cva-assets";
export const FOLDER_DRAG_TYPE = "application/x-cva-folder";

/** All folders of the organization (flat, the tree comes from `parentId`). */
export function useMediaFolders(orgId: string, enabled = true) {
  const query = useQuery({
    ...orpc.media.folders.list.queryOptions({ input: { orgId } }),
    enabled,
  });
  return { folders: query.data ?? [], loaded: query.isSuccess };
}

/** Where a file lives, for search results: "Kampagne › Reels" or "Ohne Ordner". */
export function folderPathLabel(folders: readonly AssetFolder[], folderId: string | null) {
  const path = folderPath(folders, folderId);
  return path.length > 0 ? path.map((folder) => folder.name).join(" › ") : "Ohne Ordner";
}

export function countLabel(count: number, one: string, many: string) {
  return `${count.toLocaleString("de-DE")} ${count === 1 ? one : many}`;
}

/** Name for a new folder, or a new name for an existing one. */
export function FolderNameDialog({
  orgId,
  parentId = null,
  folder,
  onClose,
  onSaved,
}: {
  orgId: string;
  /** New folder: where it is created. */
  parentId?: string | null;
  /** Rename this folder. */
  folder?: AssetFolder;
  onClose: () => void;
  onSaved?: (folder: AssetFolder) => void;
}) {
  const queryClient = useQueryClient();
  const { folders } = useMediaFolders(orgId);
  const parentName = folderPath(folders, parentId).at(-1)?.name ?? MEDIA_ROOT_LABEL;
  const [name, setName] = useState(folder?.name ?? "");
  const options = {
    onSuccess: async (saved: AssetFolder) => {
      await queryClient.invalidateQueries({ queryKey: orpc.media.key() });
      toast.success(folder ? "Ordner umbenannt" : `Ordner „${saved.name}“ angelegt`);
      onSaved?.(saved);
      onClose();
    },
  };
  const create = useMutation(orpc.media.folders.create.mutationOptions(options));
  const rename = useMutation(orpc.media.folders.update.mutationOptions(options));
  const mutation = folder ? rename : create;

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (folder) rename.mutate({ orgId, folderId: folder.id, name: trimmed });
    else create.mutate({ orgId, name: trimmed, parentId });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={folder ? "Ordner umbenennen" : "Neuer Ordner"}>
        <form onSubmit={submit}>
          <Field
            label="Name"
            error={mutation.error ? errorMessage(mutation.error) : null}
            hint={folder ? undefined : `Wird in „${parentName}“ angelegt.`}
          >
            <Input
              autoFocus
              value={name}
              maxLength={FOLDER_NAME_MAX_LENGTH}
              placeholder="z. B. Kampagne Sommer"
              onChange={(event) => {
                setName(event.target.value);
                mutation.reset();
              }}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!name.trim()} loading={mutation.isPending}>
              {folder ? "Umbenennen" : "Anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Tree of all folders to pick a target (moving media or folders). */
export function FolderPickerDialog({
  orgId,
  title,
  description,
  confirmLabel = "Hierher verschieben",
  current,
  disabled,
  onPick,
  onClose,
}: {
  orgId: string;
  title: string;
  description?: string;
  confirmLabel?: string;
  /** Where the items are now (preselected, cannot be confirmed); undefined = mixed. */
  current: string | null | undefined;
  /** Folders that cannot be chosen, e.g. a folder itself and its subfolders. */
  disabled?: ReadonlySet<string>;
  onPick: (folderId: string | null) => Promise<unknown>;
  onClose: () => void;
}) {
  const { folders } = useMediaFolders(orgId);
  const [target, setTarget] = useState<string | null>(current ?? null);
  const [busy, setBusy] = useState(false);
  const rows: { id: string | null; name: string; depth: number }[] = [
    { id: null, name: MEDIA_ROOT_LABEL, depth: 0 },
    ...folderTree(folders).map(({ folder, depth }) => ({
      id: folder.id,
      name: folder.name,
      depth: depth + 1,
    })),
  ];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} description={description}>
        <div
          role="listbox"
          aria-label="Zielordner"
          className="max-h-80 overflow-y-auto rounded-lg border border-zinc-200 p-1"
        >
          {rows.map((row) => {
            const blocked = row.id !== null && disabled?.has(row.id);
            const active = row.id === target;
            const Icon = row.id === null ? Images : active ? FolderOpen : Folder;
            return (
              <button
                key={row.id ?? "root"}
                type="button"
                role="option"
                aria-selected={active}
                disabled={blocked}
                onClick={() => setTarget(row.id)}
                style={{ paddingLeft: 8 + row.depth * 18 }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm text-zinc-700 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent",
                  active && "bg-brand-50 font-medium text-brand-700 hover:bg-brand-50",
                )}
              >
                <Icon
                  className={cn("size-4 shrink-0", row.id ? "text-brand-600" : "text-zinc-500")}
                />
                <span className="truncate">{row.name}</span>
                {row.id === current ? (
                  <span className="ml-auto shrink-0 text-xs text-zinc-400">aktuell</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            disabled={current !== undefined && target === current}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onPick(target);
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** <select> with all folders, indented like a tree ("" = top level). */
export function FolderSelect({
  folders,
  value,
  onChange,
}: {
  folders: readonly AssetFolder[];
  value: string | null;
  onChange: (folderId: string | null) => void;
}) {
  return (
    <Select value={value ?? ""} onChange={(event) => onChange(event.target.value || null)}>
      <option value="">{MEDIA_ROOT_LABEL} (ohne Ordner)</option>
      {folderTree(folders).map(({ folder, depth }) => (
        <option key={folder.id} value={folder.id}>
          {`${"   ".repeat(depth + 1)}${folder.name}`}
        </option>
      ))}
    </Select>
  );
}

/**
 * Open folder of a media list in the editor panels and the media picker.
 * While searching, the list covers all folders.
 */
export function useFolderBrowser(orgId: string, search = "") {
  const { folders } = useMediaFolders(orgId);
  const [opened, setFolderId] = useState<string | null>(null);
  // A folder deleted in the meantime shows the top level again.
  const folderId = opened && folders.some((folder) => folder.id === opened) ? opened : null;
  const browsing = !search.trim();
  return {
    folders,
    folderId,
    setFolderId,
    browsing,
    /** `folderId` filter for media.list. */
    listFolderId: browsing ? folderId : undefined,
    /** Subfolders are shown above the list (then "nothing here" would be wrong). */
    hasSubfolders: browsing && childFolders(folders, folderId).length > 0,
  };
}

/**
 * Compact folder navigation above the media lists of the editor and the
 * media picker: a back row with the path and the subfolders of the current folder.
 */
export function FolderNav({
  browser,
  kinds,
  grid,
}: {
  browser: ReturnType<typeof useFolderBrowser>;
  /** Counts only these media types (the ones the panel shows). */
  kinds?: readonly AssetKind[];
  /** Folders side by side (wide lists) instead of one per row. */
  grid?: boolean;
}) {
  const { folders, folderId, setFolderId: onChange, browsing } = browser;
  const path = folderPath(folders, folderId);
  const current = path.at(-1);
  const children = childFolders(folders, current?.id ?? null);
  if (!browsing || (!current && children.length === 0)) return null;
  const count = (folder: AssetFolder) =>
    kinds
      ? kinds.reduce((sum, kind) => sum + (folder.kindCounts[kind] ?? 0), 0)
      : folder.assetCount;
  return (
    <div className="mb-2 space-y-1">
      {current ? (
        <button
          type="button"
          onClick={() => onChange(current.parentId)}
          title={[MEDIA_ROOT_LABEL, ...path.map((folder) => folder.name)].join(" › ")}
          className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-xs text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
        >
          <ArrowLeft className="size-3.5 shrink-0" />
          <span className="truncate">
            {path.length > 1 ? `… › ${path.at(-2)!.name} › ` : `${MEDIA_ROOT_LABEL} › `}
            <span className="font-medium text-zinc-900">{current.name}</span>
          </span>
        </button>
      ) : null}
      <div className={grid ? "grid grid-cols-2 gap-1.5 sm:grid-cols-4" : "space-y-1"}>
        {children.map((folder) => {
          const n = count(folder);
          return (
            <button
              key={folder.id}
              type="button"
              onClick={() => onChange(folder.id)}
              className="flex w-full items-center gap-2 rounded-md border border-zinc-200 px-2 py-1.5 text-left text-sm text-zinc-800 hover:border-brand-300 hover:bg-brand-50"
            >
              <Folder className="size-4 shrink-0 fill-brand-100 text-brand-600" />
              <span className="min-w-0 flex-1 truncate">{folder.name}</span>
              {n > 0 ? <span className="text-xs text-zinc-400">{n}</span> : null}
              <ChevronRight className="size-3.5 shrink-0 text-zinc-400" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
